import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib/index.js';
import type { IProduct, ITheme } from '@inter-ikea-kompis/types';
import { StringUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@inter-ikea-kompis/component-text';

import WoodDeclarationCardStyle from './WoodDeclarationCardStyle.js';

const WOOD_DECLARATION_CODE = '00692';

/**
 * Wood declaration card sub component.
 */

export default class WoodDeclarationCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// An object of type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Show type of wood header.
	@property({ type: Boolean })
	public showHeader = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		if (
			!this.product?.content?.complianceInformation?.find(
				(compliance) => compliance.datasetCode === WOOD_DECLARATION_CODE
			)
		) {
			return null;
		}

		return html`
			<style>
				${WoodDeclarationCardStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="container">${this.getWoodDeclarationHeader()}${this.getWoodDeclaration()}</div>
		`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}
	/**
	 * Returns the wood type and country of harvest from the product data.
	 */
	private getWoodDeclaration(): RenderResult {
		const woodDeclarationInformation = this.product?.content?.complianceInformation
			?.filter((info) => info.datasetCode === WOOD_DECLARATION_CODE)
			.map((info) => info.text);

		if (!woodDeclarationInformation) {
			return null;
		}

		// Match 0 = Everything.
		// Match 1 = Tree name.
		// Match 2 = Scientific tree name.
		// Match 3 = Country header.
		// Match 4 = Countries.
		const regexp = /[^;]*;([^(]+)\(([^)]+)[^;]*;([^:]+):[ ]*(.+)/;

		return woodDeclarationInformation.map((info) => {
			const match = info.match(regexp) || '';
			return html`<div class="row">
				${this.getComplianceHeader({
					name: match[1],
					scientificName: match[2]
				})}${this.getComplianceData({
					header: match[3],
					countries: match[4]
				})}
			</div>`;
		});
	}

	/**
	 * Returns the wood declaration.
	 */
	private getComplianceHeader(options: { name: string; scientificName: string }): RenderResult {
		return html`<kompis-text
			class="header"
			.type="${ThemeFontStyleTypeEnum.headingXSmall}"
			.theme="${this.theme}"
			>${`${options.name} (${options.scientificName})`}</kompis-text
		>`;
	}

	/**
	 * Returns the country of harvest data.
	 *
	 * @param options Options.
	 * @param options.header Country of harvest header.
	 * @param options.countries Countries.
	 */
	private getComplianceData(options: { header: string; countries: string }): RenderResult {
		const lowerCaseCountries = options.countries
			.split(',')
			.map((country) => StringUtility.capitalizeFirstLetter(country.trim().toLocaleLowerCase()))
			.join(', ');

		return html`<kompis-text class="data" .theme="${this.theme}"
			>${`${options.header.trim()}: ${lowerCaseCountries}`}</kompis-text
		>`;
	}

	/**
	 * Returns header for accordion from data.
	 */
	private getWoodDeclarationHeader(): RenderResult {
		if (!this.showHeader) {
			return '';
		}
		const header =
			this.product.content?.complianceInformation?.find((compliance) => compliance.headingText)
				?.headingText || null;

		if (!header) {
			return '';
		}

		return html`<kompis-text
			class="woodDeclarationHeader"
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.headingXSmall}"
			>${header}</kompis-text
		>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-wood-declaration-card': WoodDeclarationCard;
	}
}
